import { Component, Input, OnChanges, output } from '@angular/core';
import { RadioRowV2Option, DateV2Module, RadioV2Module } from '@schenck/rotec-components';
import {
  SchedulingMonthlyModel,
  SchedulingMonthlyDayModel,
  SchedulingMonthlyComponent,
} from './scheduling-monthly/scheduling-monthly.component';
import {
  SchedulingWeeklyModel,
  SchedulingWeeklyComponent,
} from './scheduling-weekly/scheduling-weekly.component';
import {
  SchedulingYearlyModel,
  SchedulingYearlyDayModel,
  SchedulingYearlyComponent,
} from './scheduling-yearly/scheduling-yearly.component';
import { Scheduling, TaskIntervalPattern, Month, DayOfWeek } from 'src/api';
import { TranslateDirective } from '@ngx-translate/core';
import { SchedulingDailyComponent } from './scheduling-daily/scheduling-daily.component';

enum Interval {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Yearly = 'Yearly',
}
@Component({
  selector: 'ma-scheduling-interval',
  templateUrl: './scheduling-interval.component.html',
  styleUrls: ['./scheduling-interval.component.less'],
  imports: [
    TranslateDirective,
    DateV2Module,
    RadioV2Module,
    SchedulingDailyComponent,
    SchedulingWeeklyComponent,
    SchedulingMonthlyComponent,
    SchedulingYearlyComponent,
  ],
})
export class SchedulingIntervalComponent implements OnChanges {
  @Input()
  public scheduling: Scheduling;
  public readonly schedulingChange = output<Scheduling>();

  public get interval(): typeof Interval {
    return Interval;
  }
  public intervalOptionItems: RadioRowV2Option[] = [
    { value: Interval.Daily, name: Interval.Daily },
    { value: Interval.Weekly, name: Interval.Weekly },
    { value: Interval.Monthly, name: Interval.Monthly },
    { value: Interval.Yearly, name: Interval.Yearly },
  ];
  public intervalValue: Interval = Interval.Daily;

  public minDate = new Date();
  public startDate: Date;
  public endDate: Date;

  public dailyInterval = 1;
  public weeklyScheduling: SchedulingWeeklyModel = { daysOfWeek: [DayOfWeek.Monday], interval: 1 };
  public monthlyScheduling: SchedulingMonthlyModel = { isExact: true, interval: 1, dayOfMonth: 1 };
  public yearlyScheduling: SchedulingYearlyModel = {
    isExact: true,
    interval: 1,
    dayOfMonth: 1,
    month: Month.January,
  };

  public changeIntervalType(type: Interval): void {
    this.intervalValue = type;
    this.emitSchedulingChange();
  }

  public changeDailyInterval(interval: number): void {
    this.dailyInterval = interval;
    this.emitSchedulingChange();
  }

  public changeWeeklyScheduling(weeklyScheduling: SchedulingWeeklyModel): void {
    this.weeklyScheduling = weeklyScheduling;
    this.emitSchedulingChange();
  }

  public changeMonthlyScheduling(monthlyScheduling: SchedulingMonthlyModel): void {
    this.monthlyScheduling = monthlyScheduling;
    this.emitSchedulingChange();
  }

  public changeYearlyScheduling(yearlyScheduling: SchedulingYearlyModel): void {
    this.yearlyScheduling = yearlyScheduling;
    this.emitSchedulingChange();
  }

  public changeStartDate(startDate: Date): void {
    this.startDate = startDate;
    this.emitSchedulingChange();
  }

  public changeEndDate(endDate: Date): void {
    this.endDate = endDate;
    this.emitSchedulingChange();
  }

  // ======================== input changes

  public ngOnChanges(): void {
    this.startDate = this.scheduling.startDate;
    this.endDate = this.scheduling.endDate;

    switch (this.scheduling.pattern) {
      case TaskIntervalPattern.Weekly:
        return this.setWeeklySchedule();
      case TaskIntervalPattern.Monthly:
        return this.setMonthlyExactScheduling();
      case TaskIntervalPattern.MonthlyByWeekDayAppearance:
        return this.setMonthlyDayScheduling();
      case TaskIntervalPattern.Yearly:
        return this.setYearlyExactScheduling();
      case TaskIntervalPattern.YearlyByWeekDayAppearance:
        return this.setYearlyDayScheduling();
      case TaskIntervalPattern.Daily:
      default:
        return this.setDailySchedule();
    }
  }

  private setDailySchedule(): void {
    this.intervalValue = Interval.Daily;
    this.dailyInterval = this.scheduling.interval;
  }

  private setWeeklySchedule(): void {
    this.intervalValue = Interval.Weekly;
    this.weeklyScheduling = {
      daysOfWeek: this.scheduling.weekDays,
      interval: this.scheduling.interval,
    };
  }

  private setMonthlyExactScheduling(): void {
    this.intervalValue = Interval.Monthly;
    this.monthlyScheduling = {
      isExact: true,
      dayOfMonth: this.scheduling.dayOfMonth,
      interval: this.scheduling.interval,
    };
  }

  private setMonthlyDayScheduling(): void {
    this.intervalValue = Interval.Monthly;
    this.monthlyScheduling = {
      isExact: false,
      dayOfWeek: this.scheduling.weekDays && this.scheduling.weekDays[0],
      interval: this.scheduling.interval,
      weekOfMonth: this.scheduling.appearance,
    };
  }

  private setYearlyExactScheduling(): void {
    this.intervalValue = Interval.Yearly;
    this.yearlyScheduling = {
      isExact: true,
      dayOfMonth: this.scheduling.dayOfMonth,
      interval: this.scheduling.interval,
      month: this.scheduling.month,
    };
  }

  private setYearlyDayScheduling(): void {
    this.intervalValue = Interval.Yearly;
    this.yearlyScheduling = {
      isExact: false,
      interval: this.scheduling.interval,
      month: this.scheduling.month,
      dayOfWeek: this.scheduling.weekDays && this.scheduling.weekDays[0],
      weekOfMonth: this.scheduling.appearance,
    };
  }

  // ======================== emit output

  private emitSchedulingChange(): void {
    switch (this.intervalValue) {
      case Interval.Daily:
        return this.emitDailySchedule();
      case Interval.Weekly:
        return this.emitWeekylSchedule();
      case Interval.Monthly:
        return this.emitMonthlySchedule();
      case Interval.Yearly:
        return this.emitYearlySchedule();
    }
  }

  private emitDailySchedule(): void {
    this.schedulingChange.emit({
      appearance: null,
      dayOfMonth: null,
      endDate: this.endDate,
      startDate: this.startDate,
      interval: this.dailyInterval,
      month: null,
      pattern: TaskIntervalPattern.Daily,
      weekDays: null,
    });
  }

  private emitWeekylSchedule(): void {
    this.schedulingChange.emit({
      appearance: null,
      dayOfMonth: null,
      endDate: this.endDate,
      startDate: this.startDate,
      interval: this.weeklyScheduling.interval,
      month: null,
      pattern: TaskIntervalPattern.Weekly,
      weekDays: this.weeklyScheduling.daysOfWeek,
    });
  }

  private emitMonthlySchedule(): void {
    if (this.monthlyScheduling.isExact) {
      this.schedulingChange.emit({
        appearance: null,
        dayOfMonth: this.monthlyScheduling.dayOfMonth,
        endDate: this.endDate,
        startDate: this.startDate,
        interval: this.monthlyScheduling.interval,
        month: null,
        pattern: TaskIntervalPattern.Monthly,
        weekDays: null,
      });
    } else {
      const scheduling = <SchedulingMonthlyDayModel>this.monthlyScheduling;
      this.schedulingChange.emit({
        appearance: scheduling.weekOfMonth,
        dayOfMonth: null,
        endDate: this.endDate,
        startDate: this.startDate,
        interval: scheduling.interval,
        month: null,
        pattern: TaskIntervalPattern.MonthlyByWeekDayAppearance,
        weekDays: [scheduling.dayOfWeek],
      });
    }
  }

  private emitYearlySchedule(): void {
    if (this.yearlyScheduling.isExact) {
      this.schedulingChange.emit({
        appearance: null,
        dayOfMonth: this.yearlyScheduling.dayOfMonth,
        endDate: this.endDate,
        startDate: this.startDate,
        interval: this.yearlyScheduling.interval,
        month: this.yearlyScheduling.month,
        pattern: TaskIntervalPattern.Yearly,
        weekDays: null,
      });
    } else {
      const scheduling = <SchedulingYearlyDayModel>this.yearlyScheduling;
      this.schedulingChange.emit({
        appearance: scheduling.weekOfMonth,
        dayOfMonth: null,
        endDate: this.endDate,
        startDate: this.startDate,
        interval: scheduling.interval,
        month: scheduling.month,
        pattern: TaskIntervalPattern.YearlyByWeekDayAppearance,
        weekDays: [scheduling.dayOfWeek],
      });
    }
  }
}
